import { useEffect, useState } from "react";
import { trackEvent } from "../lib/analytics";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import Layout from "../components/Layout";
import { CheckCircle, Lightning, ArrowLeft } from "@phosphor-icons/react";

const PLANS = [
    {
        key: "standard", name: "Standard", price: "€19,90", period: "/mese", limit: "5 analisi al mese", featured: true,
        features: ["Radar aste nazionale", "5 analisi al mese (si rinnovano)", "Contatore consumo e rinnovo", "OCR perizie & import automatico"],
    },
    {
        key: "pro", name: "Pro", price: "€49,90", period: "/mese", limit: "25 analisi al mese",
        features: ["Tutto del piano Standard", "25 analisi al mese", "Confronto opportunità & export", "Supporto prioritario"],
    },
];

export default function ChangePlan() {
    const navigate = useNavigate();
    const [quota, setQuota] = useState(null);
    const [sub, setSub] = useState(null);
    const [changing, setChanging] = useState(null);
    const [confirmCancel, setConfirmCancel] = useState(false);

    const load = () => Promise.all([
        api.get("/account/quota").then((r) => setQuota(r.data)).catch(() => {}),
        api.get("/payments/subscription").then((r) => setSub(r.data)).catch(() => setSub({ has_subscription: false })),
    ]);
    useEffect(() => { load(); trackEvent("pricing_viewed"); }, []);

    const fmtDate = (d) => (d ? d.split("-").reverse().join("/") : "");

    const choose = async (plan) => {
        setChanging(plan);
        try {
            if (sub?.has_subscription) {
                const { data } = await api.post("/payments/change-subscription", { plan });
                toast.success(`Piano ${data.plan} attivato con addebito proporzionale gestito da Stripe`);
                await load();
            } else {
                const { data } = await api.post("/payments/checkout", {
                    plan, origin_url: window.location.origin,
                });
                window.location.href = data.checkout_url;
                return;
            }
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setChanging(null);
        }
    };

    const doCancel = async () => {
        setChanging("cancel");
        try {
            const { data } = await api.post("/payments/cancel-subscription");
            toast.success(`Disdetta registrata: il piano resta attivo fino al ${fmtDate(data.active_until)}`);
            setConfirmCancel(false);
            await load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setChanging(null);
        }
    };

    const doReactivate = async () => {
        setChanging("reactivate");
        try {
            await api.post("/payments/reactivate-subscription");
            toast.success("Abbonamento riattivato: nessuna interruzione al rinnovo");
            await load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setChanging(null);
        }
    };

    return (
        <Layout>
            <div className="max-w-[1000px] mx-auto" data-testid="change-plan-page">
                <button onClick={() => navigate(-1)} data-testid="change-plan-back" className="text-xs text-gray-500 hover:text-black flex items-center gap-1 mb-4">
                    <ArrowLeft size={13} /> Indietro
                </button>
                <div className="overline mb-2">Abbonamento</div>
                <h1 className="font-display-black text-3xl sm:text-4xl mb-2">Gestisci il tuo piano</h1>
                {quota && (
                    <p className="text-sm text-gray-600 mb-8" data-testid="current-plan-summary">
                        {quota.requires_plan ? (
                            <>Nessun piano attivo — scegli un piano per iniziare ad analizzare le opportunità.</>
                        ) : (
                            <>
                                Piano attuale: <b>{quota.plan_label}</b>
                                {!quota.unlimited && <> · {quota.used}/{quota.limit} analisi utilizzate{quota.one_time ? " (una tantum)" : " questo mese"}</>}
                                {quota.next_renewal && <> · prossimo rinnovo il {quota.next_renewal.split("-").reverse().join("/")}</>}
                            </>
                        )}
                    </p>
                )}
                {sub?.has_subscription && (
                    <div className="border border-gray-200 bg-white p-5 mb-8" data-testid="subscription-manage-card">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <div>
                                <div className="overline mb-1">Abbonamento attivo</div>
                                <div className="text-sm text-gray-700">
                                    Piano <b className="uppercase">{sub.plan}</b>
                                    {sub.amount != null && <> · €{(sub.amount / 100).toFixed(2).replace(".", ",")}/mese</>}
                                    {sub.current_period_end && (
                                        sub.cancel_at_period_end
                                            ? <> · <span className="text-amber-700 font-semibold" data-testid="subscription-canceling-notice">disdetto — attivo fino al {fmtDate(sub.current_period_end)}</span></>
                                            : <> · si rinnova il {fmtDate(sub.current_period_end)}</>
                                    )}
                                </div>
                            </div>
                            {sub.cancel_at_period_end ? (
                                <button onClick={doReactivate} disabled={changing !== null} data-testid="reactivate-subscription-btn"
                                        className="h-10 px-5 text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">
                                    {changing === "reactivate" ? "Riattivazione…" : "Riattiva abbonamento"}
                                </button>
                            ) : (
                                <button onClick={() => setConfirmCancel(true)} disabled={changing !== null} data-testid="cancel-subscription-btn"
                                        className="h-10 px-5 text-sm font-semibold border border-gray-300 text-gray-600 hover:border-red-400 hover:text-red-600 disabled:opacity-50">
                                    Disdici abbonamento
                                </button>
                            )}
                        </div>
                        {confirmCancel && (
                            <div className="mt-4 border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900" data-testid="cancel-confirm-box">
                                <p className="font-semibold mb-1">Confermi la disdetta?</p>
                                <p className="text-xs mb-3">
                                    Nessun altro addebito: il piano {sub.plan} resta attivo fino al {fmtDate(sub.current_period_end)},
                                    poi l'accesso alle analisi viene bloccato finché non riattivi un piano. Puoi riattivare quando vuoi prima della scadenza.
                                </p>
                                <div className="flex gap-3">
                                    <button onClick={doCancel} disabled={changing !== null} data-testid="cancel-confirm-yes"
                                            className="h-9 px-4 text-xs font-semibold bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">
                                        {changing === "cancel" ? "Disdetta…" : "Sì, disdici"}
                                    </button>
                                    <button onClick={() => setConfirmCancel(false)} data-testid="cancel-confirm-no"
                                            className="h-9 px-4 text-xs font-semibold border border-gray-300 text-gray-600">
                                        No, mantieni il piano
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
                {!quota ? (
                    <div className="flex items-center justify-center py-20" data-testid="plans-loading">
                        <div className="w-8 h-8 border-2 border-[#7C3AED] border-t-transparent rounded-full animate-spin" />
                    </div>
                ) : quota.unlimited ? (
                    <div className="border border-emerald-200 bg-emerald-50 p-6 text-sm text-emerald-900" data-testid="unlimited-plan-notice">
                        Il tuo account ha analisi <b>illimitate</b> (piano amministratore): non è necessario cambiare piano.
                    </div>
                ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start max-w-3xl">
                    {PLANS.map((p) => {
                        const current = quota?.plan === p.key;
                        return (
                            <div key={p.key} data-testid={`plan-card-${p.key}`}
                                 className={`p-6 border-2 bg-white flex flex-col h-full relative ${p.featured ? "border-emerald-600" : "border-gray-200"} ${current ? "ring-2 ring-black ring-offset-2" : ""}`}>
                                {p.featured && (
                                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-600 text-white text-[10px] uppercase tracking-widest px-3 py-1 font-bold">Consigliato</span>
                                )}
                                <h3 className="font-display-black text-xl">{p.name}</h3>
                                <div className="mt-2 flex items-end gap-1">
                                    <span className="font-display-black text-3xl">{p.price}</span>
                                    {p.period && <span className="text-xs text-gray-400 mb-1">{p.period}</span>}
                                </div>
                                <div className="mt-1 text-sm font-semibold text-emerald-700">{p.limit}</div>
                                <ul className="flex flex-col gap-2 mt-5 mb-6 flex-grow">
                                    {p.features.map((f) => (
                                        <li key={f} className="flex items-start gap-2 text-xs text-gray-600">
                                            <CheckCircle size={14} weight="fill" className="text-emerald-600 shrink-0 mt-0.5" /> {f}
                                        </li>
                                    ))}
                                </ul>
                                <button
                                    data-testid={`choose-plan-${p.key}`}
                                    disabled={current || changing !== null}
                                    onClick={() => choose(p.key)}
                                    className={`h-11 w-full text-sm font-semibold transition-colors disabled:opacity-50 ${current ? "bg-gray-100 text-gray-500" : p.featured ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-black text-white hover:bg-gray-800"}`}
                                >
                                    {current ? "Piano attuale" : changing === p.key ? "Attivazione…" : (
                                        <span className="inline-flex items-center gap-2"><Lightning size={14} weight="fill" />
                                            {sub?.has_subscription ? `Passa a ${p.name} (pro-rata)` : `Scegli ${p.name}`}
                                        </span>
                                    )}
                                </button>
                            </div>
                        );
                    })}
                </div>
                )}
                <p className="text-xs text-gray-400 mt-6" data-testid="payment-notice">
                    Pagamento sicuro tramite Stripe · Prezzi IVA esclusa · cambio piano con fatturazione
                    proporzionale automatica · disdetta in qualsiasi momento, il piano resta attivo fino a fine periodo.
                </p>
            </div>
        </Layout>
    );
}
